import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { useMemo, useState } from 'react';

import {
  DEFAULT_COUNTRY,
  sortedCountries,
} from '@/constants/countries';
import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';
import {
  formatNationalPhoneInput,
  getPhoneExampleForCountry,
  isValidPhoneForCountry,
  nationalDisplayFromE164,
  normalizePhoneForCountry,
} from '@/utils/phone';

import { Listbox } from './listbox';
import type { PopoverPortalTarget } from './popover-portal';

/**
 * Phone entry that produces E.164 and nothing else.
 *
 * A church's phone numbers are the messaging module's address book and the check-in
 * module's identity key, so "0244123456" and "+233244123456" being the same person
 * matters more here than it does in most systems. The country is chosen explicitly
 * rather than guessed from the digits, because a Ghanaian number typed without a
 * prefix is indistinguishable from several others.
 *
 * Validation matches Weive embed: libphonenumber `isValid` for the selected country,
 * national formatting as you type, and a hard cap at the country's possible length.
 *
 * Reference: documentation/architecture/widget-architecture.md §3.1.
 */

export interface PhoneInputProps {
  /** E.164, or null when the field is empty or incomplete. */
  value: string | null;
  onChange: (e164: string | null) => void;
  /** The starting country; falls back to the church's country, then Ghana. */
  defaultCountry?: CountryCode;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

export const PhoneInput = ({
  value,
  onChange,
  defaultCountry = DEFAULT_COUNTRY,
  placeholder,
  disabled = false,
  invalid = false,
  id,
  searchPlaceholder,
  emptyMessage,
  popoverPortal = 'body',
  className,
  ...aria
}: PhoneInputProps) => {
  const locale = usePreferences((state) => state.locale);

  const parsed = value ? parsePhoneNumberFromString(value) : undefined;
  const [country, setCountry] = useState<CountryCode>(
    parsed?.country ?? defaultCountry,
  );
  const [national, setNational] = useState(() =>
    value ? nationalDisplayFromE164(value) : '',
  );
  const [blurred, setBlurred] = useState(false);

  const countries = useMemo(() => sortedCountries(locale), [locale]);
  const options = useMemo(
    () =>
      countries.map((entry) => ({
        value: entry.code,
        label: `${entry.flag} ${entry.name}`,
        description: entry.dial,
        keywords: [entry.dial, entry.code],
      })),
    [countries],
  );

  const example = useMemo(() => getPhoneExampleForCountry(country), [country]);
  const draftValid = !national.trim() || isValidPhoneForCountry(national, country);
  const showInvalid = invalid || (blurred && !draftValid);

  const emit = (nextCountry: CountryCode, nextNational: string) => {
    if (!nextNational.trim()) {
      onChange(null);
      return;
    }
    onChange(normalizePhoneForCountry(nextNational, nextCountry));
  };

  return (
    <div className={cn('flex w-full items-stretch gap-2', className)}>
      <div className="shrink-0">
        <Listbox<CountryCode>
          value={country}
          onChange={(next) => {
            if (!next) return;
            setCountry(next);
            const reformatted = formatNationalPhoneInput(national, next);
            setNational(reformatted);
            emit(next, reformatted);
          }}
          options={options}
          searchable
          searchPlaceholder={searchPlaceholder}
          emptyMessage={emptyMessage}
          disabled={disabled}
          invalid={showInvalid}
          popoverPortal={popoverPortal}
          compact
          renderValue={(option) => (
            <span className="flex items-center gap-1.5">
              <span aria-hidden>{option.label.split(' ')[0]}</span>
              <span>{option.description}</span>
            </span>
          )}
        />
      </div>

      <div
        className={cn(
          'flex h-10 min-w-0 flex-1 items-center rounded-md border bg-surface px-3',
          'focus-within:border-brand',
          showInvalid ? 'border-danger' : 'border-border hover:border-border-strong',
          disabled && 'bg-surface-sunken',
        )}
      >
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          disabled={disabled}
          value={national}
          placeholder={placeholder ?? example}
          aria-invalid={showInvalid || undefined}
          onBlur={() => setBlurred(true)}
          onChange={(event) => {
            const next = formatNationalPhoneInput(event.target.value, country);
            setNational(next);
            emit(country, next);
          }}
          className={cn(
            'min-w-0 flex-1 bg-transparent text-fg outline-none',
            'placeholder:text-fg-tertiary disabled:cursor-not-allowed disabled:text-fg-disabled',
          )}
          {...aria}
        />
      </div>
    </div>
  );
};
